import type { ServiceStandardByIdResult } from "@/modules/service-standard/domain/types";
import { Badge } from "@devhop/ui";
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  MessageSquareIcon,
  XCircleIcon,
} from "lucide-react";

type ServiceStandardDetailViewProps = {
  standard: NonNullable<ServiceStandardByIdResult>;
};

const serviceStageLabels: Record<string, string> = {
  before: "ກ່ອນບໍລິການ",
  during: "ລະຫວ່າງບໍລິການ",
  after: "ຫຼັງບໍລິການ",
  all: "ທຸກເວລາ",
};

const statusLabels: Record<string, string> = {
  active: "ໃຊ້ງານ",
  draft: "ປັບປຸງ",
  inactive: "ເລີກໃຊ້",
};

const statusColors: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  active: "default",
  draft: "secondary",
  inactive: "destructive",
};

export function ServiceStandardDetailView({
  standard,
}: ServiceStandardDetailViewProps) {
  const doItems =
    standard.items?.filter((item) => item.itemType === "do") ?? [];
  const dontItems =
    standard.items?.filter((item) => item.itemType === "dont") ?? [];

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Section 1: ข้อมูลพื้นฐาน */}
      <section
        className="space-y-4 rounded-lg border bg-card p-6"
        data-tourid="detail-basic-info"
      >
        <h2 className="font-bold text-xl">ຂໍ້ມູນພື້ນຖານ</h2>

        <div className="space-y-3">
          <div>
            <h3 className="mb-1 font-medium text-muted-foreground text-sm">
              ຊື່ມາດຕະຖານ
            </h3>
            <p className="font-semibold text-lg">{standard.name}</p>
          </div>

          {standard.description && (
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ຄຳອະທິບາຍ
              </h3>
              <p className="text-sm">{standard.description}</p>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ຊ່ອງທາງບໍລິການ
              </h3>
              <Badge variant="outline" className="mt-1">
                {standard.serviceChannelName || "-"}
              </Badge>
            </div>

            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ຕໍາແໜ່ງ
              </h3>
              <p className="text-sm">
                {standard.positionTitle || "ທຸກຕໍາແໜ່ງໃນຜະແນກ"}
              </p>
            </div>

            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ຜະແນກ
              </h3>
              <p className="text-sm">{standard.departmentName || "-"}</p>
            </div>

            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ໃຊ້ເວລາໃດ
              </h3>
              <Badge variant="secondary" className="mt-1">
                {serviceStageLabels[standard.serviceStage] ||
                  standard.serviceStage}
              </Badge>
            </div>
          </div>

          <div>
            <h3 className="mb-1 font-medium text-muted-foreground text-sm">
              ສະຖານະ
            </h3>
            <Badge
              variant={statusColors[standard.status] || "secondary"}
              className="mt-1"
            >
              {statusLabels[standard.status] || standard.status}
            </Badge>
          </div>
        </div>
      </section>

      {/* Section 2: สิ่งที่ต้องทำ (Do) */}
      <section
        className="space-y-4 rounded-lg border bg-card p-6"
        data-tourid="detail-do-items"
      >
        <div className="flex items-center gap-2">
          <CheckCircle2Icon className="h-5 w-5 text-green-600" />
          <h2 className="font-bold text-xl">ສິ່ງທີ່ຕ້ອງເຮັດ</h2>
        </div>

        {doItems.length > 0 ? (
          <ul className="space-y-3">
            {doItems.map((item, index) => (
              <li key={item.id} className="flex items-start gap-3">
                <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-100 font-medium text-green-700 text-sm">
                  {index + 1}
                </span>
                <div className="flex-1">
                  <p className="font-medium">{item.content}</p>
                  {item.description && (
                    <p className="mt-1 text-muted-foreground text-sm">
                      {item.description}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
        )}
      </section>

      {/* Section 3: สิ่งที่ห้ามทำ (Don't) */}
      <section
        className="space-y-4 rounded-lg border bg-card p-6"
        data-tourid="detail-dont-items"
      >
        <div className="flex items-center gap-2">
          <XCircleIcon className="h-5 w-5 text-red-600" />
          <h2 className="font-bold text-xl">ສິ່ງທີ່ຫ້າມເຮັດ</h2>
        </div>

        {dontItems.length > 0 ? (
          <ul className="space-y-3">
            {dontItems.map((item, index) => (
              <li key={item.id} className="flex items-start gap-3">
                <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-100 font-medium text-red-700 text-sm">
                  {index + 1}
                </span>
                <div className="flex-1">
                  <p className="font-medium">{item.content}</p>
                  {item.description && (
                    <p className="mt-1 text-muted-foreground text-sm">
                      {item.description}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
        )}
      </section>

      {/* Section 4: ตัวอย่างประโยค */}
      <section
        className="space-y-4 rounded-lg border bg-card p-6"
        data-tourid="detail-examples"
      >
        <div className="flex items-center gap-2">
          <MessageSquareIcon className="h-5 w-5 text-blue-600" />
          <h2 className="font-bold text-xl">ຕົວຢ່າງປະໂຫຍກ</h2>
        </div>

        {standard.examples && standard.examples.length > 0 ? (
          <div className="space-y-4">
            {standard.examples.map((example) => (
              <div
                key={example.id}
                className="rounded-lg border-blue-500 border-l-4 bg-blue-50 p-4"
              >
                <h3 className="mb-2 font-semibold text-blue-900 text-sm">
                  {example.situation}
                </h3>
                <p className="mb-1 text-sm">{example.example}</p>
                {example.description && (
                  <p className="text-muted-foreground text-xs">
                    {example.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
        )}
      </section>

      {/* Section 5: กรณีพิเศษ */}
      <section
        className="space-y-4 rounded-lg border bg-card p-6"
        data-tourid="detail-special-cases"
      >
        <div className="flex items-center gap-2">
          <AlertTriangleIcon className="h-5 w-5 text-amber-600" />
          <h2 className="font-bold text-xl">ກໍລະນີພິເສດ</h2>
        </div>

        {standard.specialCases && standard.specialCases.length > 0 ? (
          <div className="space-y-4">
            {standard.specialCases.map((specialCase) => (
              <div
                key={specialCase.id}
                className="rounded-lg border-amber-500 border-l-4 bg-amber-50 p-4"
              >
                <h3 className="mb-2 font-semibold text-amber-900 text-sm">
                  {specialCase.title}
                </h3>
                <p className="mb-1 text-sm">{specialCase.handling}</p>
                {specialCase.description && (
                  <p className="text-muted-foreground text-xs">
                    {specialCase.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
        )}
      </section>
    </div>
  );
}
